import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateGnrSupplierMutation, useUpdateGnrSupplierMutation } from '../../../../hooks/mutations/useGnrSupplierMutation';
import { GnrSupplier } from '../../../../services/apiSLTP/sites/gnrSuppliers';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    gnrSupplier: GnrSupplier | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateGnrSupplierModal: React.FC<ModalProps> = ({ gnrSupplier, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();
    const [name, setName] = useState(gnrSupplier?.name || '');
    const [codeProC, setCodeProC] = useState(gnrSupplier?.codeProC || '');
    const [email, setEmail] = useState(gnrSupplier?.email || '');
    const [area, setArea] = useState(gnrSupplier?.area || '');
    const [phone, setPhone] = useState(gnrSupplier?.phone || '');
    const [status, setStatus] = useState(gnrSupplier?.crudStatus || crudStatusEnum.OK);

    const { mutate: mutateGnrSupplier } = useUpdateGnrSupplierMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error updating gnrSupplier:', error.message),
    });

    const { mutate: addGnrSupplier } = useCreateGnrSupplierMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error adding gnrSupplier:', error.message),
    });

    const handleSubmit = () => {
        if (isEditMode && gnrSupplier) {
            mutateGnrSupplier({ gnrSupplierId: gnrSupplier?._id, data: { name, crudStatus: status, area, email, phone, codeProC } });
        } else {
            addGnrSupplier({ name, crudStatus: status, area, phone, email, codeProC });
        }
    };

    return (
        <ModalBody
            title={t('gnrSupplier')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!(name.length && codeProC.length && email.length && area.length)}
            onSubmit={handleSubmit}
        >
            <CustomTextInput value={name} label={t('name')} onChange={(text) => setName(text)} errorText={t('error')} required />
            <CustomTextInput value={codeProC} label={t('codeProC')} onChange={(text) => setCodeProC(text)} errorText={t('error')} required />

            <ToggleLine
                checked={status === crudStatusEnum.OK}
                onChange={(checked) => setStatus(checked ? crudStatusEnum.OK : crudStatusEnum.INACTIVE)}
                title={capitalizeFirstLetter(t('active'))}
            />

            <CustomTextInput value={area} label={t('area')} onChange={(text) => setArea(text)} errorText={t('error')} required />
            <CustomTextInput value={email} label={t('email')} onChange={(text) => setEmail(text)} errorText={t('error')} required type="email" />
            <CustomTextInput value={phone} label={t('phone')} onChange={(text) => setPhone(text)} errorText={t('error')} type="tel" />
        </ModalBody>
    );
};

export default UpdateGnrSupplierModal;
